import { PlusOutlined } from '@ant-design/icons';
import { App, Button, Select, Space, Tag } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '@/services/api/client';
import * as departmentsApi from '@/services/api/departments';
import type { DepartmentTreeNode } from '@/types/department';
import { flattenDepartmentTree, collectDescendantIds, findDepartmentNode } from '@/utils/department-tree';

interface ProjectDepartmentSelectorProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
}

export function ProjectDepartmentSelector({
  value = [],
  onChange,
  disabled = false,
}: ProjectDepartmentSelectorProps) {
  const { message } = App.useApp();
  const [tree, setTree] = useState<DepartmentTreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [pickId, setPickId] = useState<string | undefined>();

  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const data = await departmentsApi.fetchDepartmentTree();
      setTree(data);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载组织架构失败';
      message.error(text);
      setTree([]);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    void loadTree();
  }, [loadTree]);

  const departmentMap = useMemo(() => {
    const map = new Map<string, DepartmentTreeNode>();
    for (const node of flattenDepartmentTree(tree)) {
      map.set(node.id, node);
    }
    return map;
  }, [tree]);

  const pickOptions = useMemo(
    () =>
      flattenDepartmentTree(tree)
        .filter((node) => !value.includes(node.id))
        .map((node) => ({
          value: node.id,
          label: `${node.name}（${node.code}）`,
        })),
    [tree, value],
  );

  const handleAdd = () => {
    if (!pickId) {
      message.warning('请先选择部门');
      return;
    }
    if (value.includes(pickId)) {
      message.warning('该部门已添加');
      return;
    }
    const node = findDepartmentNode(tree, pickId);
    const idsToAdd = node ? collectDescendantIds(node) : [pickId];
    onChange?.(Array.from(new Set([...value, ...idsToAdd])));
    setPickId(undefined);
  };

  const handleRemove = (departmentId: string) => {
    const node = findDepartmentNode(tree, departmentId);
    const idsToRemove = new Set(node ? collectDescendantIds(node) : [departmentId]);
    onChange?.(value.filter((id) => !idsToRemove.has(id)));
  };

  return (
    <div>
      <Space wrap style={{ marginBottom: value.length > 0 ? 12 : 0 }}>
        <Select
          showSearch
          allowClear
          placeholder="部门选择"
          style={{ minWidth: 280 }}
          loading={loading}
          disabled={disabled}
          options={pickOptions}
          value={pickId}
          optionFilterProp="label"
          onChange={setPickId}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={disabled || !pickId}
          onClick={handleAdd}
        >
          添加部门
        </Button>
      </Space>
      {value.length > 0 ? (
        <Space size={[8, 8]} wrap>
          {value.map((departmentId) => {
            const department = departmentMap.get(departmentId);
            const label = department
              ? `${department.name}（${department.code}）`
              : `部门 ${departmentId}`;
            return (
              <Tag
                key={departmentId}
                closable={!disabled}
                onClose={() => handleRemove(departmentId)}
              >
                {label}
              </Tag>
            );
          })}
        </Space>
      ) : null}
    </div>
  );
}
